"use client";
/**
 * OracleTool — интерактивный инструмент гадания "да или нет"
 * Три инструмента: Оракул да/нет, Монетка, Таро да/нет
 */
import { useState } from "react";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { MAJOR_ARCANA } from "@/lib/tarot/cards";

type Tab = "oracle" | "coin" | "tarot";

// ── Oracle answers ─────────────────────────────────────────────────────────────

type OracleType = "yes" | "mostly-yes" | "no" | "mostly-no" | "unclear";

interface OracleResult {
  type: OracleType;
  label: string;
  emoji: string;
  message: string;
  bg: string;
  textColor: string;
  labelColor: string;
}

const YES_MSGS = [
  "La energía del momento es favorable. Actúa con valentía — el camino está abierto.",
  "Las circunstancias se ponen a tu favor. Es momento de dar un paso decidido.",
  "El oráculo ve un buen desenlace. Confía en tu intención.",
  "Las fuerzas están de tu lado. Puedes dejar atrás las dudas.",
  "El universo dice «sí». Quizá ya conocías esta respuesta.",
  "Este camino lleva a donde necesitas. Avanza sin vacilar.",
  "Momento favorable. Tus intenciones son puras — el resultado llegará.",
  "Sí — y ahora es el mejor momento. No lo dejes para después.",
];

const MOSTLY_YES_MSGS = [
  "Más bien sí, pero precisa los detalles. Un poco de cautela no estorba.",
  "La respuesta se inclina hacia el «sí». Confía, pero mantén la conciencia.",
  "Las señales apuntan en la dirección correcta. Un paso mostrará si el camino es el bueno.",
  "Las condiciones son favorables si estás preparado. Escucha tu intuición.",
  "Hay potencial. Tus acciones decidirán el resultado — empieza por lo pequeño.",
  "Lo más probable es que sí. Pero asegúrate de que tus metas siguen siendo tuyas.",
];

const NO_MSGS = [
  "El oráculo ve un obstáculo. Quizá ahora no es el mejor momento.",
  "La energía se resiste. Conviene revisar el enfoque o esperar.",
  "Algo importante aún no está listo. La pausa también es una respuesta.",
  "Este camino ahora no está abierto. Quizá haya una ruta mejor.",
  "El oráculo dice «no» — no como sentencia, sino como pista para cambiar de rumbo.",
  "El momento aún no ha madurado. Dale tiempo a la situación para manifestarse.",
  "No — hoy. Pero mañana la energía puede cambiar.",
];

const MOSTLY_NO_MSGS = [
  "Más bien no. Hay algo que conviene reflexionar antes de avanzar.",
  "Una pequeña duda. Quizá la pregunta requiere replantearse.",
  "Las condiciones aún no cuadran. Espera un momento más claro.",
  "La respuesta se inclina hacia el «no», pero todo depende de tu disposición.",
  "Más bien no. Escucha — tu intuición ya dice algo.",
];

const UNCLEAR_MSGS = [
  "El cuadro aún está borroso. Formula la pregunta con más precisión o pregunta más tarde.",
  "El oráculo ve niebla. Ahora no es el mejor momento para esta pregunta.",
  "La situación aún no se ha formado. Dale tiempo a manifestarse.",
  "La respuesta está oculta. Quizá tu intuición ya lo sabe — escúchala.",
  "Demasiadas variables. Vuelve a la pregunta en uno o dos días.",
];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function getOracleResult(): OracleResult {
  const r = Math.random();
  if (r < 0.28) return { type: "yes", label: "SÍ", emoji: "✨", message: pick(YES_MSGS), bg: "from-emerald-950 to-emerald-800", textColor: "text-emerald-100", labelColor: "bg-emerald-500 text-white" };
  if (r < 0.50) return { type: "mostly-yes", label: "MÁS BIEN SÍ", emoji: "🌟", message: pick(MOSTLY_YES_MSGS), bg: "from-teal-950 to-teal-800", textColor: "text-teal-100", labelColor: "bg-teal-500 text-white" };
  if (r < 0.68) return { type: "mostly-no", label: "MÁS BIEN NO", emoji: "🌙", message: pick(MOSTLY_NO_MSGS), bg: "from-amber-950 to-amber-800", textColor: "text-amber-100", labelColor: "bg-amber-500 text-white" };
  if (r < 0.87) return { type: "no", label: "NO", emoji: "🌑", message: pick(NO_MSGS), bg: "from-red-950 to-red-900", textColor: "text-red-100", labelColor: "bg-red-600 text-white" };
  return { type: "unclear", label: "AÚN NO ESTÁ CLARO", emoji: "🔮", message: pick(UNCLEAR_MSGS), bg: "from-violet-950 to-violet-900", textColor: "text-violet-100", labelColor: "bg-violet-500 text-white" };
}

// ── Tarot yes/no ───────────────────────────────────────────────────────────────

const TAROT_YN: Record<number, { answer: "yes" | "no" | "wait"; short: string }> = {
  0:  { answer: "yes",  short: "Un nuevo comienzo llama. El salto a lo desconocido — ahí está el «sí»." },
  1:  { answer: "yes",  short: "Tienes todas las herramientas. Actúa — el resultado llegará." },
  2:  { answer: "wait", short: "Escucha tu intuición. La respuesta ya está dentro de ti — más en silencio." },
  3:  { answer: "yes",  short: "La abundancia está de tu lado. Buena señal para cualquier comienzo." },
  4:  { answer: "yes",  short: "Un «sí» firme, si estás dispuesto a asumir la responsabilidad." },
  5:  { answer: "wait", short: "La tradición aconseja no apresurarse. Recurre al camino probado." },
  6:  { answer: "yes",  short: "«Sí» — si la elección concuerda con tus valores más profundos." },
  7:  { answer: "yes",  short: "La victoria se logra con perseverancia. Avanza sin detenerte." },
  8:  { answer: "yes",  short: "La fuerza interior vencerá cualquier obstáculo. Un «sí» seguro." },
  9:  { answer: "wait", short: "Hace falta tiempo para reflexionar. No te apresures — la sabiduría pide una pausa." },
  10: { answer: "yes",  short: "La rueda gira a tu favor. El momento es propicio." },
  11: { answer: "yes",  short: "Un desenlace justo. «Sí» — si tus intenciones son puras." },
  12: { answer: "wait", short: "La pausa es necesaria. Esperar no es debilidad, sino sabiduría." },
  13: { answer: "no",   short: "Algo debe terminar antes de que empiece lo nuevo." },
  14: { answer: "wait", short: "El equilibrio y la paciencia llevarán a lo necesario. No fuerces los hechos." },
  15: { answer: "no",   short: "Cuídate de las ilusiones. Revisa tus motivos antes de dar el paso." },
  16: { answer: "no",   short: "La situación es inestable. Espera — ahora no es el mejor momento." },
  17: { answer: "yes",  short: "La esperanza está justificada. La estrella ilumina el camino — ve con valentía." },
  18: { answer: "wait", short: "Niebla de incertidumbre. Espera a que el cuadro se aclare." },
  19: { answer: "yes",  short: "Un «sí» claro y alegre. El Sol no deja lugar a dudas." },
  20: { answer: "wait", short: "Tiempo de reevaluación. Una mirada honesta a ti mismo revelará la respuesta." },
  21: { answer: "yes",  short: "Cierre completo del ciclo. Un «sí» absoluto — el universo está contigo." },
};

const TAROT_CFG = {
  yes:  { label: "SÍ", emoji: "✨", bg: "from-emerald-950 to-emerald-800", labelColor: "bg-emerald-500 text-white", textColor: "text-emerald-100" },
  no:   { label: "NO", emoji: "🌑", bg: "from-red-950 to-red-900", labelColor: "bg-red-600 text-white", textColor: "text-red-100" },
  wait: { label: "ESPERA", emoji: "🌙", bg: "from-violet-950 to-violet-900", labelColor: "bg-violet-500 text-white", textColor: "text-violet-100" },
};

// ── Component ──────────────────────────────────────────────────────────────────

export function OracleTool() {
  const [tab, setTab] = useState<Tab>("oracle");

  // Oracle
  const [question, setQuestion] = useState("");
  const [oracleResult, setOracleResult] = useState<OracleResult | null>(null);
  const [oracleLoading, setOracleLoading] = useState(false);

  // Coin
  const [coinResult, setCoinResult] = useState<"heads" | "tails" | null>(null);
  const [coinFlipping, setCoinFlipping] = useState(false);

  // Tarot
  const [tarotCard, setTarotCard] = useState<(typeof MAJOR_ARCANA)[0] | null>(null);
  const [tarotYN, setTarotYN] = useState<(typeof TAROT_YN)[number] | null>(null);
  const [tarotLoading, setTarotLoading] = useState(false);

  function askOracle() {
    if (oracleLoading) return;
    setOracleLoading(true);
    setOracleResult(null);
    setTimeout(() => {
      setOracleResult(getOracleResult());
      setOracleLoading(false);
    }, 1400);
  }

  function flipCoin() {
    if (coinFlipping) return;
    setCoinFlipping(true);
    setCoinResult(null);
    setTimeout(() => {
      setCoinResult(Math.random() < 0.5 ? "heads" : "tails");
      setCoinFlipping(false);
    }, 1200);
  }

  function drawTarot() {
    if (tarotLoading) return;
    setTarotLoading(true);
    setTarotCard(null);
    setTarotYN(null);
    setTimeout(() => {
      const idx = Math.floor(Math.random() * 22);
      setTarotCard(MAJOR_ARCANA[idx]);
      setTarotYN(TAROT_YN[idx]);
      setTarotLoading(false);
    }, 1400);
  }

  const tabs: { id: Tab; label: string; emoji: string }[] = [
    { id: "oracle", label: "Oráculo", emoji: "🔮" },
    { id: "coin",   label: "Moneda", emoji: "🪙" },
    { id: "tarot",  label: "Tarot",   emoji: "🃏" },
  ];

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm">

      {/* Tab bar */}
      <div className="flex border-b border-stone-100">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 text-sm font-semibold transition-colors ${
              tab === t.id
                ? "bg-violet-50 text-violet-700 border-b-2 border-violet-500"
                : "text-stone-500 hover:text-stone-700 hover:bg-stone-50"
            }`}
          >
            <span>{t.emoji}</span>
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="p-6 sm:p-8">

        {/* ── ORACLE TAB ── */}
        {tab === "oracle" && (
          <div className="space-y-5">
            <div className="text-center space-y-1">
              <p className="text-sm text-stone-500">Formula tu pregunta y recibe la respuesta del oráculo</p>
            </div>

            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Haz tu pregunta... (opcional)"
              rows={2}
              maxLength={200}
              className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-none transition"
            />

            {/* Oracle ball */}
            <div className="flex flex-col items-center gap-5">
              <button
                onClick={askOracle}
                disabled={oracleLoading}
                className="relative group"
                aria-label="Preguntar al oráculo"
              >
                <div
                  className={`w-32 h-32 rounded-full flex items-center justify-center text-5xl transition-all duration-300 select-none ${
                    oracleLoading
                      ? "scale-95 opacity-80"
                      : "hover:scale-105 active:scale-95 cursor-pointer"
                  }`}
                  style={{
                    background: "radial-gradient(circle at 35% 35%, #7c3aed, #4c1d95 60%, #1e0a3c)",
                    boxShadow: oracleLoading
                      ? "0 0 30px rgba(124,58,237,0.6), 0 0 60px rgba(109,40,217,0.3)"
                      : "0 0 20px rgba(124,58,237,0.4), 0 0 40px rgba(109,40,217,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
                  }}
                >
                  {oracleLoading ? (
                    <Loader2 className="w-10 h-10 text-violet-300 animate-spin" />
                  ) : (
                    <span style={{ filter: "drop-shadow(0 0 8px rgba(255,255,255,0.5))" }}>🔮</span>
                  )}
                </div>
                {!oracleLoading && (
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-violet-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                    Toca
                  </div>
                )}
              </button>

              <button
                onClick={askOracle}
                disabled={oracleLoading}
                className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5 rounded-full transition-colors text-sm"
              >
                {oracleLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> El oráculo está pensando...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Preguntar al oráculo</>
                )}
              </button>
            </div>

            {/* Result */}
            {oracleResult && (
              <div
                className={`bg-gradient-to-br ${oracleResult.bg} rounded-2xl p-7 text-center space-y-3 animate-in fade-in duration-500`}
              >
                <div className="text-4xl">{oracleResult.emoji}</div>
                <div className={`inline-block ${oracleResult.labelColor} font-bold text-xl px-6 py-1.5 rounded-full tracking-wide`}>
                  {oracleResult.label}
                </div>
                {question && (
                  <p className="text-white/50 text-xs italic">«{question}»</p>
                )}
                <p className={`${oracleResult.textColor} text-sm leading-relaxed max-w-xs mx-auto`}>
                  {oracleResult.message}
                </p>
                <button
                  onClick={askOracle}
                  className="flex items-center gap-1.5 mx-auto text-white/60 hover:text-white text-xs transition-colors mt-1"
                >
                  <RefreshCw className="w-3 h-3" /> Preguntar de nuevo
                </button>
              </div>
            )}

            <p className="text-center text-[11px] text-stone-400 leading-relaxed">
              La adivinación tiene un carácter de entretenimiento. Toma tus decisiones por ti mismo.
            </p>
          </div>
        )}

        {/* ── COIN TAB ── */}
        {tab === "coin" && (
          <div className="space-y-5">
            <div className="text-center space-y-1">
              <p className="text-sm text-stone-500">Cara o cruz — la moneda decide por ti</p>
            </div>

            <div className="flex flex-col items-center gap-5">
              {/* Coin visual */}
              <div
                onClick={coinFlipping ? undefined : flipCoin}
                className={`w-32 h-32 rounded-full flex items-center justify-center text-5xl select-none transition-all duration-200 ${
                  coinFlipping
                    ? "opacity-60"
                    : "cursor-pointer hover:scale-105 active:scale-95"
                }`}
                style={{
                  background: coinResult === "heads"
                    ? "radial-gradient(circle at 35% 35%, #fbbf24, #d97706 60%, #92400e)"
                    : coinResult === "tails"
                    ? "radial-gradient(circle at 35% 35%, #e2e8f0, #94a3b8 60%, #475569)"
                    : "radial-gradient(circle at 35% 35%, #fbbf24, #d97706 60%, #92400e)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)",
                  animation: coinFlipping ? "spin 0.3s linear infinite" : undefined,
                }}
              >
                {coinFlipping ? (
                  <Loader2 className="w-10 h-10 text-amber-200 animate-spin" />
                ) : coinResult === "heads" ? (
                  "🦅"
                ) : coinResult === "tails" ? (
                  "⭐"
                ) : (
                  "🪙"
                )}
              </div>

              <button
                onClick={flipCoin}
                disabled={coinFlipping}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5 rounded-full transition-colors text-sm"
              >
                {coinFlipping ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> La moneda gira...</>
                ) : (
                  <><RefreshCw className="w-4 h-4" /> Lanzar la moneda</>
                )}
              </button>
            </div>

            {/* Coin result */}
            {coinResult && !coinFlipping && (
              <div
                className={`rounded-2xl p-7 text-center space-y-2 animate-in fade-in duration-500 ${
                  coinResult === "heads"
                    ? "bg-gradient-to-br from-amber-900 to-amber-700"
                    : "bg-gradient-to-br from-slate-800 to-slate-600"
                }`}
              >
                <div className="text-5xl">{coinResult === "heads" ? "🦅" : "⭐"}</div>
                <div className="text-white font-bold text-2xl tracking-wide">
                  {coinResult === "heads" ? "CARA" : "CRUZ"}
                </div>
                <p className="text-white/70 text-sm">
                  {coinResult === "heads"
                    ? "La suerte está de tu lado. La moneda dijo «sí»."
                    : "Piénsalo otra vez. La moneda aconseja una pausa."}
                </p>
                <button
                  onClick={flipCoin}
                  className="flex items-center gap-1.5 mx-auto text-white/60 hover:text-white text-xs transition-colors mt-1"
                >
                  <RefreshCw className="w-3 h-3" /> Lanzar otra vez
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── TAROT TAB ── */}
        {tab === "tarot" && (
          <div className="space-y-5">
            <div className="text-center space-y-1">
              <p className="text-sm text-stone-500">Una carta de los 22 Arcanos Mayores responderá a tu pregunta</p>
            </div>

            <div className="flex flex-col items-center gap-5">
              {/* Card back / draw button */}
              <div
                onClick={tarotLoading ? undefined : drawTarot}
                className={`w-28 h-40 rounded-xl flex items-center justify-center text-5xl select-none transition-all duration-200 border-2 border-violet-400/40 ${
                  tarotLoading ? "opacity-60" : "cursor-pointer hover:scale-105 active:scale-95"
                }`}
                style={{
                  background: "radial-gradient(circle at 35% 30%, #6d28d9, #4c1d95 50%, #1e0a3c)",
                  boxShadow: "0 4px 24px rgba(109,40,217,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
                }}
              >
                {tarotLoading ? (
                  <Loader2 className="w-10 h-10 text-violet-300 animate-spin" />
                ) : tarotCard ? (
                  <span className="text-5xl">{tarotCard.emoji}</span>
                ) : (
                  <span style={{ fontSize: "2.5rem" }}>✦</span>
                )}
              </div>

              <button
                onClick={drawTarot}
                disabled={tarotLoading}
                className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5 rounded-full transition-colors text-sm"
              >
                {tarotLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> La carta se revela...</>
                ) : tarotCard ? (
                  <><RefreshCw className="w-4 h-4" /> Sacar otra carta</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Sacar una carta</>
                )}
              </button>
            </div>

            {/* Tarot result */}
            {tarotCard && tarotYN && !tarotLoading && (
              <div
                className={`bg-gradient-to-br ${TAROT_CFG[tarotYN.answer].bg} rounded-2xl p-7 text-center space-y-3 animate-in fade-in duration-500`}
              >
                <div className="text-4xl">{TAROT_CFG[tarotYN.answer].emoji}</div>
                <div className={`inline-block ${TAROT_CFG[tarotYN.answer].labelColor} font-bold text-xl px-6 py-1.5 rounded-full tracking-wide`}>
                  {TAROT_CFG[tarotYN.answer].label}
                </div>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl">{tarotCard.emoji}</span>
                  <div className="text-left">
                    <div className="text-white font-semibold text-base">{tarotCard.name}</div>
                    <div className="text-white/50 text-xs">Arcano {tarotCard.number}</div>
                  </div>
                </div>
                <p className={`${TAROT_CFG[tarotYN.answer].textColor} text-sm leading-relaxed max-w-xs mx-auto`}>
                  {tarotYN.short}
                </p>
                <div className="flex flex-wrap justify-center gap-1.5 mt-1">
                  {tarotCard.keywords.slice(0, 3).map((kw) => (
                    <span key={kw} className="text-[10px] bg-white/10 border border-white/20 rounded-full px-2 py-0.5 text-white/70">
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <p className="text-center text-[11px] text-stone-400 leading-relaxed">
              Solo los Arcanos Mayores — las 22 cartas del Tarot de Waite.
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
